// Captura de TIENDAS por corrida — planilla (como el Excel). El líder programado
// en una línea es_tienda agrega varias corridas (destino, SKU, inicio/fin libres,
// unidades, operarios) y las guarda todas juntas (bulk atómico).
//
// hora_inicio/hora_fin se componen en fake-UTC con combinarFechaHoraTurno usando
// la FECHA OPERATIVA de la programación + el turno → maneja el cruce de medianoche
// (T3): un fin 00:13 con turno 22:00→06:00 cae al día calendario siguiente.
//
// Ver docs/spec-mvp/modelo/captura-tiendas.md

import { useMemo, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  combinarFechaHoraTurno,
  type RegistrosTiendaBulkCreate,
  type RegistroTienda,
} from 'schemas';

type TurnoCtx = Parameters<typeof combinarFechaHoraTurno>[2];
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Combobox, type ComboboxOption } from '../../components/ui/combobox';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select } from '../../components/ui/select';
import { Spinner } from '../../components/ui/spinner';
import { LiderLayout } from '../../components/layout/LiderLayout';
import { useAuth } from '../../lib/auth';
import { useTenantConfig } from '../../lib/api/useTenantConfig';
import { ApiError } from '../../lib/api/client';
import {
  useContextoLider,
  useCrearCorridasTiendaBulk,
  useEditarCorridaTienda,
  useInvalidarCorridaTienda,
  useRegistrosTiendaHoy,
  useTiendaCapacidades,
  useTiendaSkus,
  useTiendas,
} from '../../lib/api/queries';
import { EstadoTurnoCard } from './EstadoTurnoCard';

interface Fila {
  tienda_id: string;
  sku_id: string;
  orden_produccion: string;
  hora_inicio: string; // HH:MM
  hora_fin: string; // HH:MM
  unidades: string;
  operarios: string;
  stickers: string;
  termos: string;
}

const filaVacia = (): Fila => ({
  tienda_id: '',
  sku_id: '',
  orden_produccion: '',
  hora_inicio: '',
  hora_fin: '',
  unidades: '',
  operarios: '',
  stickers: '',
  termos: '',
});

const HORA_RE = /^([01][0-9]|2[0-3]):[0-5][0-9]$/;

export function CapturaTiendaPage() {
  const { lineaId } = useParams<{ lineaId: string }>();
  const { state } = useAuth();
  const { atribucionCruce } = useTenantConfig();
  const ctx = useContextoLider();
  const tiendas = useTiendas();
  const skus = useTiendaSkus();
  const capacidades = useTiendaCapacidades();
  const corridasHoy = useRegistrosTiendaHoy();
  const crearMut = useCrearCorridasTiendaBulk();

  const [filas, setFilas] = useState<Fila[]>([filaVacia()]);

  // Programación es_tienda de ESTA línea (autoridad de turno + fecha operativa).
  const prog = useMemo(
    () => ctx.data?.programaciones_hoy.find((p) => p.linea.id === lineaId && p.linea.es_tienda),
    [ctx.data, lineaId],
  );

  const skuOptions: ComboboxOption[] = useMemo(
    () =>
      (skus.data ?? []).map((s) => ({
        value: s.id,
        label: s.codigo_cliente,
        description: s.descripcion,
        searchText: `${s.codigo_cliente} ${s.descripcion}`,
      })),
    [skus.data],
  );

  // SKUs curados por destino (Configuración → Tiendas → Capacidades). Si un
  // destino no tiene curación, se cae a la lista completa (sin bloquear captura).
  const curadosPorTienda = useMemo(() => {
    const map = new Map<string, Set<string>>();
    for (const c of capacidades.data ?? []) {
      if (!c.es_activo) continue;
      const set = map.get(c.tienda_id) ?? new Set<string>();
      set.add(c.sku_id);
      map.set(c.tienda_id, set);
    }
    return map;
  }, [capacidades.data]);

  const opcionesSku = (tiendaId: string): ComboboxOption[] => {
    const curados = tiendaId ? curadosPorTienda.get(tiendaId) : undefined;
    if (!curados || curados.size === 0) return skuOptions;
    return skuOptions.filter((o) => curados.has(o.value));
  };

  // Cantidades de insumo por (destino, SKU) desde la curación (Configuración →
  // Tiendas → Capacidades). Prefill de stickers/termos al elegir el SKU: el líder
  // solo ajusta si la corrida difiere del estándar.
  const insumosPorClave = useMemo(() => {
    const map = new Map<string, { stickers: number; termos: number }>();
    for (const c of capacidades.data ?? []) {
      if (!c.es_activo) continue;
      map.set(`${c.tienda_id}:${c.sku_id}`, {
        stickers: c.cantidad_stickers,
        termos: c.cantidad_termos,
      });
    }
    return map;
  }, [capacidades.data]);

  const prefillInsumos = (tiendaId: string, skuId: string): Pick<Fila, 'stickers' | 'termos'> => {
    const ins = insumosPorClave.get(`${tiendaId}:${skuId}`);
    return {
      stickers: ins ? String(ins.stickers) : '',
      termos: ins ? String(ins.termos) : '',
    };
  };

  if (state.kind !== 'signed_in') return <Navigate to="/login" replace />;

  if (ctx.isLoading) {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center gap-3 p-6">
        <Spinner className="size-6" />
        <p className="text-sm text-fg-tertiary">Cargando su turno…</p>
      </main>
    );
  }

  // Sin programación es_tienda para esta línea → no autorizado / mal navegado,
  // o el día no opera / el turno arranca dentro de las próximas 12h (design
  // horario-turno-dia §6) — con `no_opera` esta línea nunca aparece en
  // `programaciones_hoy` (el backend la filtra).
  if (!ctx.data || !prog) {
    if (ctx.data && (ctx.data.proximo_inicio || ctx.data.dia_sin_turno)) {
      return (
        <LiderLayout>
          <div className="mx-auto flex max-w-md flex-col items-center gap-4 p-6 text-center">
            <EstadoTurnoCard
              proximoInicio={ctx.data.proximo_inicio}
              diaSinTurno={ctx.data.dia_sin_turno}
              ahoraLocal={`${ctx.data.fecha_hoy}T${ctx.data.hora_servidor}`}
            />
            <Link to="/capturar" className="text-sm underline text-fg-tertiary">
              Volver
            </Link>
          </div>
        </LiderLayout>
      );
    }
    return (
      <LiderLayout>
        <div className="mx-auto flex max-w-md flex-col items-center gap-4 p-6 text-center">
          <h1 className="text-lg font-medium text-fg">No está programado en tiendas hoy</h1>
          <p className="text-sm text-fg-secondary">
            Esta línea de tiendas no está en su programación de hoy. Contacte al coordinador.
          </p>
          <Link to="/capturar" className="text-sm underline text-fg-tertiary">
            Volver
          </Link>
        </div>
      </LiderLayout>
    );
  }

  const fechaOperativa = prog.fecha_operativa;
  const turno = prog.turno;

  // Duración (min) y unds/min de una fila, vía los timestamps fake-UTC compuestos.
  const calcFila = (f: Fila): { durMin: number | null; undsMin: number | null } => {
    if (!HORA_RE.test(f.hora_inicio) || !HORA_RE.test(f.hora_fin))
      return { durMin: null, undsMin: null };
    const ini = Date.parse(
      combinarFechaHoraTurno(fechaOperativa, f.hora_inicio, turno, atribucionCruce),
    );
    const fin = Date.parse(
      combinarFechaHoraTurno(fechaOperativa, f.hora_fin, turno, atribucionCruce),
    );
    const durMin = (fin - ini) / 60_000;
    if (!(durMin > 0)) return { durMin: null, undsMin: null };
    const u = Number(f.unidades);
    const undsMin = u > 0 ? u / durMin : null;
    return { durMin, undsMin };
  };

  const setFila = (i: number, patch: Partial<Fila>) =>
    setFilas((prev) => prev.map((f, idx) => (idx === i ? { ...f, ...patch } : f)));
  const agregarFila = () => setFilas((prev) => [...prev, filaVacia()]);
  const quitarFila = (i: number) =>
    setFilas((prev) => (prev.length === 1 ? prev : prev.filter((_, idx) => idx !== i)));

  const filaValida = (f: Fila): boolean => {
    if (!f.tienda_id || !f.sku_id) return false;
    // D9: la OC de tiendas es numeración SAP de 10 dígitos (4503…).
    if (!/^\d{10}$/.test(f.orden_produccion.trim())) return false;
    if (!HORA_RE.test(f.hora_inicio) || !HORA_RE.test(f.hora_fin)) return false;
    if (!(Number(f.unidades) > 0)) return false;
    // Operarios: REQUERIDO (entero 1..60) — necesario para el costo de mano de obra.
    const op = Number(f.operarios);
    if (!f.operarios || !Number.isInteger(op) || op < 1 || op > 60) return false;
    // Stickers/termos: opcionales (prefill desde curación), pero si vienen deben
    // ser enteros >= 0.
    for (const v of [f.stickers, f.termos]) {
      if (v === '') continue;
      const n = Number(v);
      if (!Number.isInteger(n) || n < 0) return false;
    }
    const { durMin } = calcFila(f);
    return durMin !== null && durMin > 0;
  };

  const todasValidas = filas.every(filaValida);

  const guardar = async () => {
    if (!todasValidas) {
      toast.error(
        'Revise las filas: destino, SKU, orden de 10 dígitos (4503…), horas válidas, unidades > 0 y operarios (1–60).',
      );
      return;
    }
    const payload: RegistrosTiendaBulkCreate = {
      corridas: filas.map((f) => ({
        tienda_id: f.tienda_id,
        sku_id: f.sku_id,
        orden_produccion: f.orden_produccion.trim(),
        turno_id: turno.id, // el backend valida que matchee la programación (Opción B)
        hora_inicio: combinarFechaHoraTurno(fechaOperativa, f.hora_inicio, turno, atribucionCruce),
        hora_fin: combinarFechaHoraTurno(fechaOperativa, f.hora_fin, turno, atribucionCruce),
        cantidad_unidades: Number(f.unidades),
        numero_operarios: Number(f.operarios),
        cantidad_stickers: f.stickers !== '' ? Number(f.stickers) : null,
        cantidad_termos: f.termos !== '' ? Number(f.termos) : null,
        es_captura_fuera_turno: false,
        razon_fuera_turno: null,
        capturado_por_supervisor: false,
        razon_delegacion: null,
        lider_asignado_user_id: null,
      })),
    };

    try {
      const creadas = await crearMut.mutateAsync(payload);
      toast.success(`${creadas.length} corrida(s) guardada(s)`);
      setFilas([filaVacia()]);
      void corridasHoy.refetch();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'No se pudieron guardar las corridas';
      toast.error(msg);
    }
  };

  return (
    <LiderLayout>
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-5">
        <header className="flex items-start justify-between gap-2">
          <div>
            <p className="text-[10px] uppercase tracking-[0.12em] text-fg-tertiary">
              {ctx.data.tenant.nombre} · Turno {turno.numero} ({turno.hora_inicio.slice(0, 5)}–
              {turno.hora_fin.slice(0, 5)})
            </p>
            <h1 className="mt-1 text-xl font-medium text-fg">Captura de tiendas</h1>
            <p className="text-xs text-fg-tertiary">
              Cargue las corridas del turno (puede agregar varias y guardarlas juntas).
            </p>
          </div>
          {ctx.data.programaciones_hoy.length > 1 ? (
            <Link
              to="/capturar"
              className="shrink-0 rounded border border-border px-2.5 py-1 text-xs font-medium text-fg-secondary transition-colors hover:bg-surface-overlay"
            >
              Cambiar línea
            </Link>
          ) : null}
        </header>

        {/* Planilla */}
        <section className="flex flex-col gap-3">
          {filas.map((f, i) => {
            const { durMin, undsMin } = calcFila(f);
            return (
              <Card key={i}>
                <CardContent className="grid grid-cols-2 gap-3 p-4 sm:grid-cols-12">
                  <div className="col-span-2 sm:col-span-3">
                    <Label htmlFor={`destino-${i}`}>Destino</Label>
                    <Select
                      id={`destino-${i}`}
                      value={f.tienda_id}
                      onChange={(e) =>
                        setFila(i, {
                          tienda_id: e.target.value,
                          sku_id: '',
                          stickers: '',
                          termos: '',
                        })
                      }
                      className="mt-1"
                    >
                      <option value="">— Elija —</option>
                      {(tiendas.data ?? []).map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.nombre}
                        </option>
                      ))}
                    </Select>
                  </div>
                  <div className="col-span-2 sm:col-span-3">
                    <Label>SKU</Label>
                    <Combobox
                      options={opcionesSku(f.tienda_id)}
                      value={f.sku_id}
                      onChange={(v) => setFila(i, { sku_id: v, ...prefillInsumos(f.tienda_id, v) })}
                      placeholder={f.tienda_id ? 'Buscar SKU…' : 'Elija destino primero'}
                    />
                  </div>
                  <div className="col-span-2 sm:col-span-2">
                    <Label htmlFor={`orden-${i}`}>Orden prod.</Label>
                    <Input
                      id={`orden-${i}`}
                      value={f.orden_produccion}
                      onChange={(e) =>
                        setFila(i, { orden_produccion: e.target.value.replace(/\D/g, '') })
                      }
                      inputMode="numeric"
                      maxLength={10}
                      placeholder="10 dígitos (4503…)"
                      className="mt-1"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <Label htmlFor={`ini-${i}`}>Inicio</Label>
                    <Input
                      id={`ini-${i}`}
                      type="time"
                      value={f.hora_inicio}
                      onChange={(e) => setFila(i, { hora_inicio: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <Label htmlFor={`fin-${i}`}>Fin</Label>
                    <Input
                      id={`fin-${i}`}
                      type="time"
                      value={f.hora_fin}
                      onChange={(e) => setFila(i, { hora_fin: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div className="sm:col-span-1">
                    <Label htmlFor={`unds-${i}`}>Unds</Label>
                    <Input
                      id={`unds-${i}`}
                      type="number"
                      inputMode="numeric"
                      min={1}
                      value={f.unidades}
                      onChange={(e) => setFila(i, { unidades: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div className="sm:col-span-1">
                    <Label htmlFor={`oper-${i}`}>Oper.</Label>
                    <Input
                      id={`oper-${i}`}
                      type="number"
                      inputMode="numeric"
                      min={1}
                      value={f.operarios}
                      onChange={(e) => setFila(i, { operarios: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div className="sm:col-span-1">
                    <Label htmlFor={`stk-${i}`}>Stick.</Label>
                    <Input
                      id={`stk-${i}`}
                      type="number"
                      inputMode="numeric"
                      min={0}
                      value={f.stickers}
                      onChange={(e) => setFila(i, { stickers: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div className="sm:col-span-1">
                    <Label htmlFor={`trm-${i}`}>Termo</Label>
                    <Input
                      id={`trm-${i}`}
                      type="number"
                      inputMode="numeric"
                      min={0}
                      value={f.termos}
                      onChange={(e) => setFila(i, { termos: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div className="col-span-2 flex items-center justify-between sm:col-span-12">
                    <p className="text-xs text-fg-tertiary">
                      {durMin !== null
                        ? `Duración ${Math.round(durMin)} min${undsMin !== null ? ` · ${undsMin.toFixed(1)} u/min` : ''}`
                        : 'Complete horas válidas (fin posterior al inicio)'}
                    </p>
                    <button
                      type="button"
                      onClick={() => quitarFila(i)}
                      disabled={filas.length === 1}
                      className="text-xs text-fg-tertiary underline disabled:opacity-40"
                    >
                      Quitar
                    </button>
                  </div>
                </CardContent>
              </Card>
            );
          })}

          <div className="flex items-center justify-between">
            <Button variant="outline" onClick={agregarFila}>
              + Agregar corrida
            </Button>
            <Button onClick={guardar} disabled={!todasValidas || crearMut.isPending}>
              {crearMut.isPending ? 'Guardando…' : `Guardar todo (${filas.length})`}
            </Button>
          </div>
        </section>

        {/* Corridas del día */}
        <section className="mt-2">
          <h2 className="mb-2 text-sm font-medium text-fg">Corridas de hoy</h2>
          {corridasHoy.isLoading ? (
            <Spinner className="size-4" />
          ) : (corridasHoy.data ?? []).length === 0 ? (
            <p className="text-sm text-fg-tertiary">Todavía no ha cargado corridas hoy.</p>
          ) : (
            <div className="flex flex-col divide-y divide-border rounded-md border border-border">
              {(corridasHoy.data ?? []).map((c) => (
                <CorridaHoyItem
                  key={c.id}
                  corrida={c}
                  tiendas={tiendas.data ?? []}
                  opcionesSku={opcionesSku}
                  fechaOperativa={fechaOperativa}
                  turno={turno}
                />
              ))}
            </div>
          )}
        </section>
      </div>
    </LiderLayout>
  );
}

// Una corrida de hoy: vista + edición/invalidación inline (líder dueño, dentro de
// la ventana). La editabilidad la decide el backend (corrida.editable_por_lider).
function CorridaHoyItem({
  corrida: c,
  tiendas,
  opcionesSku,
  fechaOperativa,
  turno,
}: {
  corrida: RegistroTienda;
  tiendas: { id: string; nombre: string }[];
  opcionesSku: (tiendaId: string) => ComboboxOption[];
  fechaOperativa: string;
  turno: TurnoCtx;
}) {
  const { atribucionCruce } = useTenantConfig();
  const [modo, setModo] = useState<'ver' | 'editar' | 'invalidar'>('ver');
  const [tiendaId, setTiendaId] = useState(c.tienda_id);
  const [skuId, setSkuId] = useState(c.sku_id);
  const [orden, setOrden] = useState(c.orden_produccion ?? '');
  const [ini, setIni] = useState(c.hora_inicio.slice(11, 16));
  const [fin, setFin] = useState(c.hora_fin.slice(11, 16));
  const [unds, setUnds] = useState(String(Number(c.cantidad_unidades)));
  const [oper, setOper] = useState(c.numero_operarios != null ? String(c.numero_operarios) : '');
  const [stk, setStk] = useState(c.cantidad_stickers != null ? String(c.cantidad_stickers) : '');
  const [trm, setTrm] = useState(c.cantidad_termos != null ? String(c.cantidad_termos) : '');
  const [razon, setRazon] = useState('');

  const editarMut = useEditarCorridaTienda();
  const invalidarMut = useInvalidarCorridaTienda();

  const resetEdit = () => {
    setTiendaId(c.tienda_id);
    setSkuId(c.sku_id);
    setOrden(c.orden_produccion ?? '');
    setIni(c.hora_inicio.slice(11, 16));
    setFin(c.hora_fin.slice(11, 16));
    setUnds(String(Number(c.cantidad_unidades)));
    setOper(c.numero_operarios != null ? String(c.numero_operarios) : '');
    setStk(c.cantidad_stickers != null ? String(c.cantidad_stickers) : '');
    setTrm(c.cantidad_termos != null ? String(c.cantidad_termos) : '');
  };

  const guardar = async () => {
    if (!tiendaId || !skuId || !orden.trim()) {
      toast.error('Destino, SKU y orden de producción son obligatorios.');
      return;
    }
    if (!HORA_RE.test(ini) || !HORA_RE.test(fin)) {
      toast.error('Horas inválidas.');
      return;
    }
    if (!(Number(unds) > 0)) {
      toast.error('Unidades debe ser mayor a 0.');
      return;
    }
    for (const [v, nombre] of [
      [stk, 'Stickers'],
      [trm, 'Termos'],
    ] as const) {
      if (v !== '' && (!Number.isInteger(Number(v)) || Number(v) < 0)) {
        toast.error(`${nombre} debe ser un entero mayor o igual a 0.`);
        return;
      }
    }
    try {
      await editarMut.mutateAsync({
        id: c.id,
        patch: {
          tienda_id: tiendaId,
          sku_id: skuId,
          orden_produccion: orden.trim(),
          hora_inicio: combinarFechaHoraTurno(fechaOperativa, ini, turno, atribucionCruce),
          hora_fin: combinarFechaHoraTurno(fechaOperativa, fin, turno, atribucionCruce),
          cantidad_unidades: Number(unds),
          numero_operarios: oper ? Number(oper) : null,
          cantidad_stickers: stk !== '' ? Number(stk) : null,
          cantidad_termos: trm !== '' ? Number(trm) : null,
        },
      });
      toast.success('Corrida actualizada.');
      setModo('ver');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'No se pudo actualizar.');
    }
  };

  const invalidar = async () => {
    if (razon.trim().length < 20) {
      toast.error('La razón debe tener al menos 20 caracteres.');
      return;
    }
    try {
      await invalidarMut.mutateAsync({ id: c.id, razon: { razon_invalidacion: razon.trim() } });
      toast.success('Corrida invalidada.');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'No se pudo invalidar.');
    }
  };

  if (modo === 'editar') {
    return (
      <div className="flex flex-col gap-2 px-3 py-3">
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-12">
          <div className="col-span-2 sm:col-span-3">
            <Label htmlFor={`e-dest-${c.id}`}>Destino</Label>
            <Select
              id={`e-dest-${c.id}`}
              value={tiendaId}
              onChange={(e) => {
                setTiendaId(e.target.value);
                setSkuId('');
              }}
              className="mt-1"
            >
              <option value="">— Elija —</option>
              {tiendas.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.nombre}
                </option>
              ))}
            </Select>
          </div>
          <div className="col-span-2 sm:col-span-3">
            <Label>SKU</Label>
            <Combobox
              options={opcionesSku(tiendaId)}
              value={skuId}
              onChange={setSkuId}
              placeholder="Buscar SKU…"
            />
          </div>
          <div className="col-span-2 sm:col-span-2">
            <Label htmlFor={`e-ord-${c.id}`}>Orden prod.</Label>
            <Input
              id={`e-ord-${c.id}`}
              value={orden}
              onChange={(e) => setOrden(e.target.value)}
              className="mt-1"
            />
          </div>
          <div className="sm:col-span-2">
            <Label htmlFor={`e-ini-${c.id}`}>Inicio</Label>
            <Input
              id={`e-ini-${c.id}`}
              type="time"
              value={ini}
              onChange={(e) => setIni(e.target.value)}
              className="mt-1"
            />
          </div>
          <div className="sm:col-span-2">
            <Label htmlFor={`e-fin-${c.id}`}>Fin</Label>
            <Input
              id={`e-fin-${c.id}`}
              type="time"
              value={fin}
              onChange={(e) => setFin(e.target.value)}
              className="mt-1"
            />
          </div>
          <div className="sm:col-span-1">
            <Label htmlFor={`e-unds-${c.id}`}>Unds</Label>
            <Input
              id={`e-unds-${c.id}`}
              type="number"
              min={1}
              value={unds}
              onChange={(e) => setUnds(e.target.value)}
              className="mt-1"
            />
          </div>
          <div className="sm:col-span-1">
            <Label htmlFor={`e-op-${c.id}`}>Oper.</Label>
            <Input
              id={`e-op-${c.id}`}
              type="number"
              min={1}
              value={oper}
              onChange={(e) => setOper(e.target.value)}
              className="mt-1"
            />
          </div>
          <div className="sm:col-span-1">
            <Label htmlFor={`e-stk-${c.id}`}>Stick.</Label>
            <Input
              id={`e-stk-${c.id}`}
              type="number"
              min={0}
              value={stk}
              onChange={(e) => setStk(e.target.value)}
              className="mt-1"
            />
          </div>
          <div className="sm:col-span-1">
            <Label htmlFor={`e-trm-${c.id}`}>Termo</Label>
            <Input
              id={`e-trm-${c.id}`}
              type="number"
              min={0}
              value={trm}
              onChange={(e) => setTrm(e.target.value)}
              className="mt-1"
            />
          </div>
        </div>
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => {
              resetEdit();
              setModo('ver');
            }}
            className="text-xs text-fg-tertiary underline"
          >
            Cancelar
          </button>
          <Button onClick={guardar} disabled={editarMut.isPending}>
            {editarMut.isPending ? 'Guardando…' : 'Guardar'}
          </Button>
        </div>
      </div>
    );
  }

  if (modo === 'invalidar') {
    return (
      <div className="flex flex-col gap-2 px-3 py-3">
        <p className="text-sm text-fg">
          Invalidar corrida {c.hora_inicio.slice(11, 16)}–{c.hora_fin.slice(11, 16)} ·{' '}
          {c.tienda_nombre ?? ''}
        </p>
        <textarea
          value={razon}
          onChange={(e) => setRazon(e.target.value)}
          placeholder="Motivo de la invalidación (mínimo 20 caracteres)"
          rows={2}
          className="w-full rounded-md border border-border bg-transparent px-3 py-2 text-sm text-fg"
        />
        <div className="flex items-center justify-between">
          <span className="text-xs text-fg-tertiary">{razon.trim().length}/20</span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                setRazon('');
                setModo('ver');
              }}
              className="text-xs text-fg-tertiary underline"
            >
              Cancelar
            </button>
            <Button
              variant="outline"
              onClick={invalidar}
              disabled={invalidarMut.isPending || razon.trim().length < 20}
            >
              {invalidarMut.isPending ? 'Invalidando…' : 'Confirmar invalidación'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between gap-2 px-3 py-2 text-sm">
      <span className="text-fg">
        {c.hora_inicio.slice(11, 16)}–{c.hora_fin.slice(11, 16)} · {c.tienda_nombre ?? '—'}
        {c.orden_produccion ? ` · OP ${c.orden_produccion}` : ''}
      </span>
      <span className="hidden flex-1 truncate text-fg-secondary sm:block">
        {c.sku_descripcion ?? ''}
      </span>
      <span className="shrink-0 text-fg-tertiary">
        {Number(c.cantidad_unidades)} u · {Math.round(c.duracion_minutos)} min ·{' '}
        {c.productividad_unds_min.toFixed(1)}/min
        {c.cumplimiento_pct != null ? ` · ${c.cumplimiento_pct}%` : ''}
      </span>
      {c.editable_por_lider ? (
        <span className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={() => setModo('editar')}
            className="text-xs text-accent underline"
          >
            Editar
          </button>
          <button
            type="button"
            onClick={() => setModo('invalidar')}
            className="text-xs text-crit underline"
          >
            Invalidar
          </button>
        </span>
      ) : null}
    </div>
  );
}
